import { ArrowLeft, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { endMetadataMerge } from "../../lib/metadata-history/dispatch";
import { builderSessionId, builderSteps, useMetadataBuilderStore } from "../../stores/metadata-builder-store";
import { Button } from "../ui/button";
import { PanelLayout } from "../ui/panel-layout";
import { useEffect } from "react";
import { useMetadataHistoryStore } from "../../stores/metadata-history-store";
import { ArtworkUpload } from "./ArtworkUpload";
import { ArtistCreditStep } from "./ArtistCreditStep";
import { WeekDatesStep } from "./WeekDatesStep";
import { OfflineStep } from "./OfflineStep";
import { StreamStep } from "./StreamStep";
import { streamLayoutComplete } from "../../lib/metadata-builder/stream-layout";
import { oneSlotComplete } from "../../lib/metadata-builder/one-slot-layout";
import { ReviewStep } from "./ReviewStep";
import { BuilderStepNav } from "./BuilderStepNav";
import { DevOverlayControls } from "../editor-panel/developer-section/DevOverlayControls";

export function MetadataBuilderPanel() {
  const { t } = useTranslation();
  const step = useMetadataBuilderStore(state => state.step);
  const setStep = useMetadataBuilderStore(state => state.setStep);
  const uploading = useMetadataBuilderStore(state => state.uploading);
  const issues = useMetadataBuilderStore(state => state.uploadIssues);
  const artwork = useMetadataHistoryStore(state => state.sessions[builderSessionId]?.artwork);
  const metadata = useMetadataHistoryStore(state => state.sessions[builderSessionId]?.projection);
  const canContinue = !!artwork && !uploading && issues.length === 0;
  const hasRequiredStreamElements = oneSlotComplete(metadata);
  const hasRequiredTwoStreamElements = streamLayoutComplete(metadata, "two_slot");
  const index = builderSteps.indexOf(step);
  const canNavigate = (target: number) => {
    if (target > 0 && !canContinue) return false;
    if (target > index && target > builderSteps.indexOf("oneSlot") && !hasRequiredStreamElements) return false;
    if (target > index && target > builderSteps.indexOf("twoSlot") && !hasRequiredTwoStreamElements) return false;
    return true;
  };
  useEffect(() => {
    if (!artwork) setStep("upload");
  }, [artwork, setStep]);
  const steps = {
    upload: { title: t("builder.step.upload"), help: t("builder.help.upload") },
    credit: { title: t("builder.step.credit"), help: t("builder.help.credit") },
    dates: { title: t("builder.step.dates"), help: t("builder.help.dates") },
    offline: { title: t("builder.step.offline"), help: t("builder.help.offline") },
    oneSlot: { title: t("builder.step.oneSlot"), help: t("builder.help.oneSlot") },
    twoSlot: { title: t("builder.step.twoSlot"), help: t("builder.help.twoSlot") },
    review: { title: t("builder.step.review"), help: t("builder.help.review") },
  };
  const navigate = (target: number) => {
    const next = builderSteps[target];
    if (!next) return;
    if (!canNavigate(target)) return;
    endMetadataMerge();
    setStep(next);
  };

  return (
    <PanelLayout
      className="h-full"
      bodyClassName="space-y-3 pb-3"
      footerClassName="justify-between"
      footer={<>
        <Button variant="action" size="sm" disabled={index === 0} onClick={() => navigate(index - 1)}>
          <ArrowLeft />{t("builder.back")}
        </Button>
        <Button variant="action" size="sm" disabled={!canNavigate(index + 1) || index === builderSteps.length - 1} onClick={() => navigate(index + 1)}>
          {t("builder.next")}<ArrowRight />
        </Button>
      </>}
      header={<div className="space-y-3 border-b border-neutral-200 pb-3 pr-2">
        <h2 className="text-sm font-semibold">{t("builder.title")}</h2>
        <DevOverlayControls />
      </div>}
    >
      <BuilderStepNav step={step} steps={steps} canNavigate={canNavigate} navigate={navigate} />
      <p>{steps[step].help}</p>
      {step === "upload" && <ArtworkUpload />}
      {step === "credit" && <ArtistCreditStep />}
      {step === "dates" && <WeekDatesStep />}
      {step === "offline" && <OfflineStep />}
      {step === "oneSlot" && <StreamStep key="one_slot" mode="one_slot" />}
      {step === "twoSlot" && <StreamStep key="two_slot" mode="two_slot" />}
      {step === "review" && <ReviewStep />}
    </PanelLayout>
  );
}
